import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, ArrowLeft, Check, MapPin } from "lucide-react";

import LiveLayout from "./LiveLayout";
import D13Footer from "../preview/D13Footer";
import { cld } from "../../lib/cloudinary";
import { SECTORS, getSectorBySlug } from "../../lib/sectors";
import "./SectorPage.css";

const easeOut = [0.22, 1, 0.36, 1];

export default function SectorPage() {
  const { slug } = useParams();
  const sector = getSectorBySlug(slug);
  const prefersReducedMotion = useReducedMotion();

  if (!sector) return <Navigate to="/employers#sectors" replace />;

  /* sibling sectors for the "Explore other sectors" footer */
  const others = SECTORS.filter((s) => s.slug !== slug).slice(0, 6);

  return (
    <LiveLayout
      audience="employer"
      sections={[
        { label: "Overview",  href: "#overview" },
        { label: "Roles",     href: "#roles" },
        { label: "Cities",    href: "#cities" },
        { label: "Brief us",  href: "#brief" },
      ]}
    >
      {/* ───── BREADCRUMB ───── */}
      <div className="sp__crumb">
        <div className="sp__crumb-inner">
          <Link to="/employers#sectors" className="sp__crumb-link">
            <ArrowLeft size={14} strokeWidth={2.4} /> All sectors
          </Link>
          <span className="sp__crumb-sep">·</span>
          <span className="sp__crumb-here">Sector</span>
        </div>
      </div>

      {/* ───── HERO ───── */}
      <section id="overview" className="sp__hero">
        <img
          src={cld(`adel/sector-${sector.slug}`, { w: 1920, q: "auto", f: "auto" })}
          srcSet={`${cld(`adel/sector-${sector.slug}`, { w: 960 })} 960w, ${cld(`adel/sector-${sector.slug}`, { w: 1440 })} 1440w, ${cld(`adel/sector-${sector.slug}`, { w: 1920 })} 1920w`}
          sizes="100vw"
          alt={sector.name}
          className="sp__hero-img"
        />
        <div className="sp__hero-veil" />

        <div className="sp__container sp__hero-inner">
          <motion.span
            className="d13-eyebrow d13-eyebrow--on-dark"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Sector · For employers
          </motion.span>
          <motion.h1
            className="sp__hero-title"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: easeOut }}
          >
            {sector.name}
          </motion.h1>
          <motion.p
            className="sp__hero-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
          >
            {sector.blurb}
          </motion.p>
          <motion.div
            className="sp__hero-stats"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div>
              <span className="sp__hero-stat-v">{sector.count.toLocaleString()}+</span>
              <span className="sp__hero-stat-l">Placed in this sector</span>
            </div>
            <div>
              <span className="sp__hero-stat-v">{sector.cities.length}</span>
              <span className="sp__hero-stat-l">Active destination cities</span>
            </div>
            <div>
              <span className="sp__hero-stat-v">{sector.roles.length}</span>
              <span className="sp__hero-stat-l">Role types staffed</span>
            </div>
          </motion.div>
          <motion.div
            className="sp__hero-ctas"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
          >
            <a href="#brief" className="d13-btn d13-btn--primary">
              Brief us on a {sector.short.toLowerCase()} role <ArrowRight size={16} strokeWidth={2.2} />
            </a>
            <a href="#roles" className="d13-btn d13-btn--outline">
              See role types <ArrowUpRight size={16} strokeWidth={2.2} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* ───── INTRO STATEMENT ───── */}
      <section className="sp__intro">
        <div className="sp__container">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.8 }}
            className="sp__intro-text"
          >
            {sector.intro}
          </motion.p>
        </div>
      </section>

      {/* ───── ROLES STAFFED ───── */}
      <section id="roles" className="sp__roles">
        <div className="sp__container">
          <header className="sp__roles-head">
            <span className="d13-eyebrow">Roles we staff</span>
            <h2 className="sp__h2">
              {sector.roles.length} role types in <em>{sector.short.toLowerCase()}.</em>
            </h2>
          </header>
          <motion.ul
            className="sp__roles-grid"
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            variants={{ show: { transition: { staggerChildren: 0.05 } } }}
          >
            {sector.roles.map((r, i) => (
              <motion.li
                key={r}
                className="sp__role"
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOut } },
                }}
              >
                <span className="sp__role-idx">{String(i + 1).padStart(2, "0")}</span>
                <span className="sp__role-name">{r}</span>
                <Check size={16} strokeWidth={2.4} className="sp__role-check" />
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* ───── KEY CITIES ───── */}
      <section id="cities" className="sp__cities">
        <div className="sp__container">
          <header className="sp__roles-head">
            <span className="d13-eyebrow">Where we place them</span>
            <h2 className="sp__h2">
              Active cities for <em>{sector.short.toLowerCase()}.</em>
            </h2>
          </header>
          <ul className="sp__cities-list">
            {sector.cities.map((c) => (
              <li key={c} className="sp__city">
                <MapPin size={14} strokeWidth={2.2} />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───── BRIEF FORM ───── */}
      <section id="brief" className="d13-aud-form">
        <div className="d13-container">
          <div className="d13-aud-form__head">
            <span className="d13-eyebrow">Brief intake</span>
            <h2 className="d13-h2">Tell us about your {sector.short.toLowerCase()} role.</h2>
            <p className="d13-aud-form__sub">
              We'll come back in writing within 48 hours with realistic salary bands, lead times and a starter shortlist for this sector.
            </p>
          </div>

          <form className="d13-aud-form__grid" onSubmit={(e) => e.preventDefault()}>
            <label><span>Your name</span><input type="text" placeholder="Full name" /></label>
            <label><span>Company</span><input type="text" placeholder="Company name" /></label>
            <label><span>Work email</span><input type="email" placeholder="name@company.com" /></label>
            <label><span>Country</span><input type="text" placeholder="UAE · KSA · Qatar …" /></label>
            <label className="d13-aud-form__full">
              <span>Role / specifics</span>
              <input type="text" placeholder={`e.g. 12 ${sector.roles[0].toLowerCase()}, Doha`} />
            </label>
            <label className="d13-aud-form__full">
              <span>Numbers + start date</span>
              <input type="text" placeholder="e.g. 12 by August 15" />
            </label>
            <label className="d13-aud-form__full">
              <span>Anything else?</span>
              <textarea rows={4} placeholder="Project context, certifications required, accommodation arrangements…" />
            </label>
            <button type="submit" className="d13-btn d13-btn--primary">
              Send brief <ArrowRight size={16} strokeWidth={2.2} />
            </button>
          </form>
        </div>
      </section>

      {/* ───── EXPLORE OTHER SECTORS ───── */}
      <section className="sp__related">
        <div className="sp__container">
          <header className="sp__related-head">
            <span className="d13-eyebrow">Explore other sectors</span>
            <h2 className="sp__h2">We staff seventeen sectors.</h2>
            <Link to="/employers#sectors" className="sp__related-all">
              Back to all sectors <ArrowUpRight size={14} strokeWidth={2.2} />
            </Link>
          </header>
          <ul className="sp__related-grid">
            {others.map((s) => (
              <motion.li
                key={s.slug}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, ease: easeOut }}
              >
                <Link to={`/sectors/${s.slug}`} className="sp__related-card">
                  <img
                    src={cld(`adel/sector-${s.slug}`, { w: 800, q: "auto", f: "auto" })}
                    alt=""
                    loading="lazy"
                  />
                  <div className="sp__related-veil" />
                  <span className="sp__related-name">{s.name}</span>
                  <span className="sp__related-arrow"><ArrowUpRight size={16} /></span>
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* ───── CLOSE ───── */}
      <section className="d13-close">
        <div className="d13-container">
          <motion.h2 className="d13-close__t"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.9 }}>
            Prefer to talk first?
          </motion.h2>
          <p className="d13-close__p">Our Nairobi team is on the phone Monday–Saturday, 08:00–17:00 EAT.</p>
          <div className="d13-close__ctas">
            <Link to="/employers" className="d13-btn d13-btn--ghost">← Back to employers</Link>
            <a href="tel:+254117222777" className="d13-btn d13-btn--primary">
              Call +254 117 222 777 <ArrowRight size={16} strokeWidth={2.2} />
            </a>
          </div>
        </div>
      </section>

      <D13Footer />
    </LiveLayout>
  );
}
